import { functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';

export class FunctionsService {
    /**
     * Get sales report from Cloud Function (Legacy/Compat signature)
     * En nuestro caso, esto podría llamar a useDynamiaERP logic o directamente a la function.
     * Dado que se usa en Hooks, lo ideal es que useSalesReport maneje la lógica.
     * Pero algunos componentes pueden llamar a FunctionsService directamente para otras cosas.
     */
    static async getSalesReport(from, to, forceShopify = false) {
        // Esta función se usa principalmente dentro de useSalesReport.
        // Si la necesitamos 'fuera', duplicamos la lógica de llamada a la function fetchDynamiaSalesData
        // O simplemente lanzamos un error diciendo "Use useSalesReport hook".
        console.warn('FunctionsService.getSalesReport called directly. Prefer useSalesReport hook.');
        return { error: 'Use useSalesReport hook instead' };
    }

    static async saveBudgetConfig(budgetConfig) {
        // Implementación real llamando a Firebase Function si existe, o simulando
        try {
            const callable = httpsCallable(functions, 'saveBudgetConfig');
            const result = await callable({ budgetConfig });
            return { data: result.data };
        } catch (error) {
            console.error('Error saving budget config:', error);
            return { error: error.message };
        }
    }

    static async clearCache(dateRange) {
        try {
            const callable = httpsCallable(functions, 'clearCache');
            // Adapt payload
            const payload = dateRange
                ? { dateRange: { from: new Date(dateRange.from).toISOString(), to: new Date(dateRange.to).toISOString() } }
                : {};
            const result = await callable(payload);
            return { data: result.data };
        } catch (error) {
            console.error('Error clearing cache:', error);
            return { error: error.message };
        }
    }

    static async getProductsByIds(productIds) {
        // Compatibilidad: Si Dynamia no tiene productos, retornamos vacío o implementamos la function
        // Asumiremos que la function 'getProductsByIds' existe en el backend actual (MIGRACION_COMPLETA decía que se reescribió todo)
        // Si no existe, fallará.
        try {
            const callable = httpsCallable(functions, 'getProductsByIds');
            const result = await callable({ productIds });
            return { data: result.data || {} };
        } catch (error) {
            console.warn('getProductsByIds failed, returning empty', error);
            return { data: {} };
        }
    }

    static async getAllProducts() {
        try {
            const callable = httpsCallable(functions, 'getAllProducts');
            const result = await callable();
            return { data: result.data };
        } catch (error) {
            console.error('getAllProducts failed', error);
            return { error: error.message };
        }
    }

    static async getProductsFromFirestore() {
        try {
            const callable = httpsCallable(functions, 'getProductsFromFirestore');
            const result = await callable();
            return { data: result.data };
        } catch (error) {
            console.error('getProductsFromFirestore failed', error);
            return { error: error.message };
        }
    }

    static async getProductsByCollection(data) {
        try {
            const callable = httpsCallable(functions, 'getProductsByCollection');
            const result = await callable(data);
            return { data: result.data };
        } catch (error) {
            console.error('getProductsByCollection failed', error);
            return { error: error.message };
        }
    }

    static async getProductById(productIdOrTitle) {
        try {
            const callable = httpsCallable(functions, 'getProductById');
            const result = await callable({ productIdOrTitle });
            return { data: result.data };
        } catch (error) {
            console.error('getProductById failed', error);
            return { error: error.message };
        }
    }

    static async clearProductsCache() {
        try {
            const callable = httpsCallable(functions, 'clearProductsCache');
            const result = await callable();
            return { data: result.data };
        } catch (error) {
            console.error('clearProductsCache failed', error);
            return { error: error.message };
        }
    }

    static async syncProductsToFirestore() {
        try {
            const callable = httpsCallable(functions, 'syncProductsToFirestore');
            const result = await callable();
            return { data: result.data };
        } catch (error) {
            console.error('syncProductsToFirestore failed', error);
            return { error: error.message };
        }
    }

    static async importProductsFromJSON(products) {
        try {
            const callable = httpsCallable(functions, 'importProductsFromJSON');
            const result = await callable({ products });
            return { data: result.data };
        } catch (error) {
            console.error('importProductsFromJSON failed', error);
            return { error: error.message };
        }
    }

    static async getSellersOverview() {
        try {
            const callable = httpsCallable(functions, 'getSellersOverview');
            const result = await callable();
            return { data: result.data };
        } catch (error) {
            console.error('getSellersOverview failed', error);
            return { error: error.message };
        }
    }

    static async saveSeller(seller) {
        try {
            const callable = httpsCallable(functions, 'saveSeller');
            const result = await callable(seller);
            return { data: result.data };
        } catch (error) {
            console.error('saveSeller failed', error);
            return { error: error.message };
        }
    }

    static async deleteSeller(id) {
        try {
            const callable = httpsCallable(functions, 'deleteSeller');
            const result = await callable({ id });
            return { data: result.data };
        } catch (error) {
            console.error('deleteSeller failed', error);
            return { error: error.message };
        }
    }
}
